import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Panel, Stat, Layout, LazyBoi, Amount, GateKeeper, Field } from '@components'
import { Asset } from '@archetypes'
import styled from "styled-components"
import { RedeemAllController } from './_controller'
import appController, { Status } from '@app/App.Controller'
import { ReactComponent as Refresh } from '@assets/Icon_Refresh.svg';
import { roundDown } from "@util/helpers"
import { ReactComponent as IconInfo } from "@assets/Icon_Info_Large.svg"



const Wrapper = styled.div`
	

	.group-input {
		margin-bottom: 30px;
	}

`

const InfoPanel = styled(
    ({ className, data }) => {
        return (
            <div className={className}>
                <div className="-header">
                    <div>
                        <IconInfo className="svg" />
                    </div>
                    <div>
                        <h3>
                            You are exiting your position
                        </h3>
                        <p>
                            Redeeming all your assets will unlock all staked collateral and exit your position on this asset.
                        </p>
                    </div>
                </div>
                <div className="-body">
                    {data.map((item, index) =>
                        (<div className="-row" key={index}>
                            <div>
                                {item?.key}
                            </div>
                            <div>
                                {item?.value}
                            </div>
                        </div>))}
                </div>
            </div>
        )
    })`
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 4px;
    h3 {
        font-size: 14px;
        font-weight: 600;
        color: var(--color-white);
        margin: 0px;
    }
    p {
        margin: 0px;
        font-size: 14px;
        font-weight: 600;
        color: var(--color-label, lightblue);
        line-height: 22px;
    }
    
    >.-header {
        
        padding: 20px;
        display: flex;
        flex-direction: row;
        >div {
            flex: 10%;
            :first-child {
                display: flex;
                .svg {
                    margin-left: auto;
                    margin-right: auto;
                    width: 24px;
                    height: 24px;
                    color: transparent;
                    
                }
            }
            :last-child {
                flex-direction: column;
                flex: 90%;
            }
        }
        border-bottom: 1px solid rgba(255,255,255,0.1);
    }

    >.-body {

        padding: 20px;
        padding-top: 0px;
        padding-bottom: 0px;
        >.-row {
            display: flex;
            padding-top: 20px;
            padding-bottom: 20px;
            flex-direction: row;
            :not(:last-child) {
                border-bottom: 1px solid rgba(255,255,255,0.1);
            }
            
            >div {
                flex: 1;
                font-size: 16px;
                color: var(--color-label, lightblue);
                :last-child {
                    text-align: right;
                    color: var(--color-white);
                }
            }
        }

    }

    `


const Main = ({ address, ...rest }) => {

    const { synthetics } = appController
    // approval params
    const [approvalStatus, setApprovalStatus] = useState()
    const [approvals, setApprovals] = useState()
    const [controller, setController] = useState()
    const [params, setParams] = useState()

    useEffect(() => {
        const redeemAllController = new RedeemAllController(address, appController)
        redeemAllController.on('approvals', setApprovals)
        redeemAllController.on('params', setParams)
        setController(redeemAllController)
        redeemAllController.init()
        return () => redeemAllController.cleanup()
    }, [address]) // eslint-disable-line

    const data = useMemo(() => {
        let output = []
        if (params) {


            if (params?.totalDebtAmount !== "0" && !params?.expired) {
                output.push({
                    key: "Total Assets to Redeem",
                    value: `${Number(params?.totalDebtAmount).toLocaleString()} ${params?.debtSymbol}`
                })
                output.push({
                    key: "Collateral to Unlock",
                    value: `${Number(params?.totalCollateralAmount).toLocaleString()} ${params?.symbol}`
                })
            } else {
                // Show balance when expired && totalDebtAmount === 0
                output.push({
                    key: "Total Assets to Redeem",
                    value: `${Number(params?.balance).toLocaleString()} ${params?.debtSymbol}`
                })
                output.push({
                    key: "Collateral to Unlock",
                    value: `${Number(params?.estimatedRedeemAmount).toLocaleString()} ${params?.symbol}`
                })
            }

            if (!params?.expired) {
                output.push({
                    key: "Expired",
                    value: "No"
                })
            } else {
                output.push({
                    key: "Expired",
                    value: "Yes"
                })
                let settlementStatus
                switch (params?.expiryStatus) {
                    case 0:
                        settlementStatus = "Not Settled"
                        break;
                    case 1:
                        settlementStatus = "Waiting for exit price"
                        break
                    default:
                        settlementStatus = "Ready"
                        break;
                }
                output.push({
                    key: "Settlement Status ",
                    value: settlementStatus
                })
                output.push({
                    key: "Settlement Price ",
                    value: <LazyBoi value={params?.expiryPrice} dp={2} fromWei />
                })
            }
        }
        return output
    }, [params])

    return (
        <Panel fadeIn {...rest}>
            <GateKeeper
                controllerAssertions={[
                    {
                        controller: 'account',
                        key: 'status',
                        assert: val => val === Status.READY,
                        onFail: <Panel fadeIn>Connect account to redeem your assets</Panel>,
                    },
                ]}
                assertions={[
                    {
                        assert: () => !!params,
                        onFail: () => <Panel fadeIn><Refresh className='rotate' />Fetching position information</Panel>,
                    }
                ]}
                dependencies={[params]}
            >
                <Wrapper>
                    <InfoPanel
                        data={data}
                    />

                    {!params?.expired
                        ?
                        (<Asset.ContractApproval.Wizard
                            approvals={approvals}
                            onUpdate={setApprovalStatus}
                            actionText={"Redeem & Exit Position"}
                            onAction={() => synthetics.redeem(params?.txRedeemAll)}
                        />)
                        :
                        (<Asset.ContractApproval.Wizard
                            approvals={approvals}
                            onUpdate={setApprovalStatus}
                            actionText={"Redeem & Exit Position"}
                            onAction={() => synthetics.redeemAll(params?.txRedeemAllAfterExpired)} // After expired
                        />)}
                </Wrapper> 
            </GateKeeper>
        </Panel>
    )

}


const RedeemAll = ({ className }) => {
    const { address } = useParams();

    return <Layout back title='Redeem all Assets' className={className}>
        <Layout.Aside>
            <Asset.Position address={address} />
            <Asset.Stats address={address} />
        </Layout.Aside>
        <Layout.Main>
            <Main address={address} />
        </Layout.Main>
    </Layout>
}

RedeemAll.Panel = Main

export default RedeemAll
